import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, Send, Clock3, AlertTriangle, RefreshCw, Radio, WifiOff } from "lucide-react";
import { api } from "@/lib/api";
import { useRealtime } from "@/contexts/RealtimeContext";

/**
 * Live 24 h GHL SMS delivery snapshot for /admin/status.
 *
 * Data source: GET /api/ghl/sms-report?hours=24. Auto-refresh every 30 s
 * matching the parent StatusPage cadence. Two big number tiles (sent · queued)
 * plus a per-action / per-kind breakdown so ops can spot where the queue
 * came from (magic-links, delinquency nudges, admin broadcasts, etc).
 */
export function SmsDeliveryCard() {
  const { status: rtStatus } = useRealtime();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data: d } = await api.get("/ghl/sms-report?hours=24");
      setData(d);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [load]);

  const sent = data?.sent_total ?? 0;
  const queued = data?.queued_total ?? 0;
  const state = queued > 0 && sent === 0 ? "warn" : "ok";
  const stateCls = state === "warn"
    ? "border-amber-500/40 text-amber-300 bg-amber-500/10"
    : "border-emerald-500/40 text-emerald-300 bg-emerald-500/10";

  return (
    <Card
      className="border-white/10 bg-card p-4 md:col-span-2"
      data-testid="sms-delivery-card"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-orange-400" />
          <div className="font-medium">SMS Delivery (last 24 h)</div>
        </div>
        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            className={
              rtStatus === "online"
                ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
                : rtStatus === "reconnecting" || rtStatus === "connecting"
                ? "border-amber-500/40 text-amber-300 bg-amber-500/10"
                : "border-zinc-500/40 text-zinc-400 bg-zinc-500/10"
            }
            data-testid="sms-delivery-live-pill"
            title={
              rtStatus === "online"
                ? "WebSocket + Redis pub/sub healthy — events push in real time"
                : `Realtime status: ${rtStatus}`
            }
          >
            {rtStatus === "online" ? (
              <>
                <Radio className="h-2.5 w-2.5 mr-1 animate-pulse" />
                Live
              </>
            ) : rtStatus === "reconnecting" || rtStatus === "connecting" ? (
              <>
                <Radio className="h-2.5 w-2.5 mr-1" />
                {rtStatus}
              </>
            ) : (
              <>
                <WifiOff className="h-2.5 w-2.5 mr-1" />
                Offline
              </>
            )}
          </Badge>
          <Badge variant="outline" className={stateCls} data-testid="sms-delivery-badge">
            {data?.ghl_configured === false ? "GHL not configured" : (state === "ok" ? "Healthy" : "Backlog")}
          </Badge>
          <button
            onClick={load}
            disabled={loading}
            className="text-xs text-zinc-400 hover:text-zinc-200 flex items-center gap-1"
            data-testid="sms-delivery-refresh"
          >
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {err && (
        <div className="text-xs text-red-400 mb-2" data-testid="sms-delivery-error">{err}</div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div
          className="rounded-lg border border-white/5 bg-white/[0.02] p-3"
          data-testid="sms-delivery-sent-tile"
        >
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500">
            <Send className="h-3 w-3" /> Sent
          </div>
          <div className="text-2xl font-semibold text-emerald-300 mt-1" data-testid="sms-delivery-sent-total">
            {sent}
          </div>
          <div className="text-[11px] text-zinc-500 mt-1">
            {Object.entries(data?.sent_by_action || {}).map(([k, v]) => (
              <div key={k} className="flex justify-between">
                <span className="text-zinc-400">{k.replace("ghl.sms.", "").replace("ghl.message.", "")}</span>
                <span>{v}</span>
              </div>
            ))}
            {sent === 0 && <div className="text-zinc-600">No sends in window.</div>}
          </div>
        </div>

        <div
          className="rounded-lg border border-white/5 bg-white/[0.02] p-3"
          data-testid="sms-delivery-queued-tile"
        >
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500">
            <Clock3 className="h-3 w-3" /> Queued
          </div>
          <div className={`text-2xl font-semibold mt-1 ${queued > 0 ? "text-amber-300" : "text-zinc-400"}`}
               data-testid="sms-delivery-queued-total">
            {queued}
          </div>
          <div className="text-[11px] text-zinc-500 mt-1">
            {Object.entries(data?.queued_by_kind || {}).map(([k, v]) => (
              <div key={k} className="flex justify-between">
                <span className="text-zinc-400">{k}</span>
                <span>{v}</span>
              </div>
            ))}
            {queued === 0 && <div className="text-zinc-600">Nothing backed up.</div>}
          </div>
        </div>
      </div>

      {data?.recent?.length > 0 && (
        <details className="mt-3">
          <summary className="text-xs text-zinc-500 cursor-pointer hover:text-zinc-300 flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" />
            Recent 15 events
          </summary>
          <div className="mt-2 space-y-1 max-h-48 overflow-y-auto" data-testid="sms-delivery-recent">
            {data.recent.map((r, i) => (
              <div key={i} className="text-[11px] flex justify-between border-b border-white/5 pb-1">
                <span className="text-zinc-400">
                  {r.action?.replace("ghl.", "")} · {r.user_name || "system"}
                </span>
                <span className={r.details?.ok ? "text-emerald-400" : "text-amber-400"}>
                  {r.details?.ok ? "sent" : "queued/fail"} · {(r.created_at || "").slice(11, 16)}
                </span>
              </div>
            ))}
          </div>
        </details>
      )}
    </Card>
  );
}

export default SmsDeliveryCard;
